import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useGetRelatedAdsQuery } from '@nimbazaar/store';
import { AdCard } from './AdCard';

interface RelatedAdsProps {
  adId: string;
  categoryName: string;
  categorySlug: string;
}

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function formatBudget(min?: number, max?: number): string {
  const format = (n: number) => {
    if (n >= 10000000) {
      return `Rs. ${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Crore`;
    } else if (n >= 100000) {
      return `Rs. ${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} Lac`;
    } else if (n >= 1000) {
      return `Rs. ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)} Thousand`;
    }
    return `Rs. ${n.toLocaleString()}`;
  };

  if (min && max) {
    return `${format(min)} - ${format(max)}`;
  } else if (min) {
    return `From ${format(min)}`;
  } else if (max) {
    return `Up to ${format(max)}`;
  }
  return 'Budget not specified';
}

export function RelatedAds({ adId, categoryName, categorySlug }: RelatedAdsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isError } = useGetRelatedAdsQuery({ adId, limit: 8 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const relatedAdsData = data?.data ?? [];

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, relatedAdsData.length]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = 240;
      const gap = 16;
      const scrollAmount = cardWidth + gap;

      if (direction === 'left') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    }
  };

  if (isLoading) {
    return (
      <section className="bg-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4">More in {categoryName}</h2>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  if (isError || !data?.data || data.data.length === 0) {
    return null;
  }

  const relatedAds = data.data;

  return (
    <section className="bg-white py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">More in {categoryName}</h2>
          <Link
            to={`/category/${categorySlug}`}
            className="text-primary-500 hover:text-primary-600 text-sm font-medium flex items-center gap-1"
          >
            See all
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Cards Carousel */}
        <div className="relative group/carousel">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-secondary-100 shadow-lg rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover/carousel:opacity-60 hover:!opacity-100 transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth px-1"
          >
            {relatedAds.map((ad) => (
              <AdCard
                key={ad.id}
                id={ad.id}
                slug={ad.slug}
                title={ad.title}
                budget={formatBudget(ad.minBudget, ad.maxBudget)}
                category={ad.category.name}
                location={ad.location}
                postedTime={formatRelativeTime(ad.createdAt)}
                bids={ad.bidCount}
                views={ad.viewCount}
                image={ad.images[0]?.url}
                urgent={false}
              />
            ))}
          </div>

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-secondary-100 shadow-lg rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover/carousel:opacity-60 hover:!opacity-100 transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
